import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { fileService } from '../../services/api';

interface FileUploadProps {
  onFileUploaded: (filename?: string) => void;
  usageStats?: {
    report_count: number;
    limit: number;
    limit_reached: boolean;
    remaining: number;
  };
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUploaded, usageStats }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [additionalQuestions, setAdditionalQuestions] = useState('');
  const [patientAge, setPatientAge] = useState('40');
  const [patientGender, setPatientGender] = useState('Male');
  const [openaiConsent, setOpenaiConsent] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        enqueueSnackbar('Please select a PDF file', { variant: 'error' });
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        enqueueSnackbar('File size must be less than 50MB', { variant: 'error' });
        return;
      }
      setSelectedFile(file);
      enqueueSnackbar('File selected successfully', { variant: 'success' });
    }
  }, [enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const handleSubmit = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a PDF file first', { variant: 'error' });
      return;
    }

    if (!openaiConsent) {
      enqueueSnackbar('You must provide consent for AI processing', { variant: 'error' });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await fileService.uploadFile(
        selectedFile,
        additionalQuestions,
        patientAge,
        patientGender
      );

      setUploadProgress(100);
      enqueueSnackbar('File uploaded successfully! Processing will begin shortly.', { 
        variant: 'success',
        autoHideDuration: 5000,
      });

      // Reset form
      setSelectedFile(null);
      setAdditionalQuestions('');
      setUploadProgress(0);
      
      // Notify parent component with filename
      onFileUploaded(selectedFile.name);

    } catch (error: any) {
      console.error('Upload error:', error);
      enqueueSnackbar(
        error.response?.data?.detail || 'Upload failed. Please try again.',
        { variant: 'error' }
      );
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
      clearInterval(progressInterval);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  // Age options (1-120)
  const ageOptions = Array.from({ length: 120 }, (_, i) => i + 1);

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          📄 Upload Your Medical Report
        </Typography>

        {/* Usage Stats Alert */}
        {usageStats && usageStats.report_count > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have <strong>{usageStats.remaining} free analysis attempts</strong> remaining 
            ({usageStats.report_count}/{usageStats.limit} used)
          </Alert>
        )}

        {/* OpenAI Consent Notice */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🤖 AI Processing Notice
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Your medical data will be processed by OpenAI (US-based AI service)</strong>
          </Typography>
          <Typography variant="body2" component="div">
            By using this service, you consent to:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Transferring your medical data to OpenAI servers in the US</li>
              <li>Processing by OpenAI's AI models for analysis generation</li>
              <li>Temporary storage on OpenAI servers during processing</li>
              <li>Data encryption during transmission and processing</li>
            </ul>
          </Typography>
          <Typography variant="body2">
            <strong>Data Protection:</strong> Your data is processed according to OpenAI's privacy policy. 
            No data is retained by OpenAI after processing.
          </Typography>
        </Alert>

        {/* File Upload Area */}
        <Box
          {...getRootProps()}
          className={`file-upload-dropzone ${isDragActive ? 'drag-active' : ''} ${isDragReject ? 'drag-reject' : ''}`}
          sx={{ mb: 3 }}
        >
          <input {...getInputProps()} />
          {selectedFile ? (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                File Selected
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Button variant="outlined" size="small" onClick={removeFile}>
                Remove File
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop your PDF file here' : 'Upload Medical Report (PDF)'}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Drag and drop your PDF file here, or click to browse
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Maximum file size: 50MB
              </Typography>
            </Box>
          )}
        </Box>

        {selectedFile && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Patient Information */}
            <Typography variant="h6" gutterBottom>
              👤 Patient Information
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
              Please provide the following information for better analysis
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Age (Required)</InputLabel>
                  <Select
                    value={patientAge}
                    label="Age (Required)"
                    onChange={(e) => setPatientAge(e.target.value)}
                  >
                    {ageOptions.map((age) => (
                      <MenuItem key={age} value={age.toString()}>
                        {age} years
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender (Required)</InputLabel>
                  <Select
                    value={patientGender}
                    label="Gender (Required)"
                    onChange={(e) => setPatientGender(e.target.value)}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Additional Questions */}
            <Typography variant="h6" gutterBottom>
              💭 Additional Questions (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
              Do you have any specific questions about your medical report?
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Your questions about the report"
              placeholder="e.g., What does this abnormal value mean? Should I be concerned about these results? How can I improve my health based on these findings?"
              value={additionalQuestions}
              onChange={(e) => setAdditionalQuestions(e.target.value)}
              sx={{ mb: 3 }}
            />

            {/* Consent Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={openaiConsent}
                  onChange={(e) => setOpenaiConsent(e.target.checked)}
                  color="primary"
                />
              }
              label="✅ I consent to OpenAI processing my medical data for AI analysis"
              sx={{ mb: 3 }}
            />

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading file... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={isUploading ? null : <Send />}
              onClick={handleSubmit}
              disabled={!openaiConsent || isUploading}
              sx={{ py: 1.5 }}
            >
              {isUploading ? 'Uploading...' : '🚀 Submit for Analysis'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FileUpload;


